import _ from 'lodash'
import { useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { skipToken } from '@reduxjs/toolkit/query'
import { createColumnHelper } from '@tanstack/react-table'
import { useSnackbar } from 'notistack'

import { Stack, Tooltip } from '@mui/material'
import BadgeIcon from '@mui/icons-material/Badge'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import ChipButton from '@/components/ui/buttons/ChipButton'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import Table from '@/components/ui/tables/Table'

import { License } from '@/core/api/license'
import { useAssignAdminLicensesMutation, useDeleteAdminMutation, useGetEstablishmentAdminsQuery } from '@/core/services/super-admin'

import AssignLicenseDrawer from '@/pages/admin/staffs/components/AssignLicensesDrawer'

import { getApiErrorMessage } from '@/utils/errors'
import { useGetLicensesQuery } from '@/core/services/license'

type AdminData = {
  id: string
  email: string
  fullName: string
  licenses: string[]
}

const columnHelper = createColumnHelper<AdminData>()

type AdminsTableProps = {}

export function AdminsTable(props: AdminsTableProps) {
  const lingui = useLingui()

  const { establishmentId } = useParams<{ establishmentId: string }>()

  const { data: admins } = useGetEstablishmentAdminsQuery(establishmentId ?? skipToken)
  const { data: allLicenses } = useGetLicensesQuery({})

  const columns = useMemo(
    () => [
      columnHelper.accessor('fullName', {
        header: lingui._(t`Full name`),
      }),
      columnHelper.accessor('email', {
        header: lingui._(t`Email`),
      }),
      columnHelper.display({
        id: 'actions',
        header: lingui._(t`Actions`),
        cell: ({ row, table }) => (
          <Stack justifyContent="center" direction="row" spacing={1}>
            <AssignLicenses admin={row.original} availableLicenses={allLicenses} />
            <DeleteAdmin adminId={row.original.id} />
          </Stack>
        ),
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ],
    [allLicenses, lingui]
  )

  const data = useMemo(
    () =>
      admins?.map((admin) => ({
        id: admin._id,
        email: admin.email,
        fullName: `${admin.userInfo.name} ${admin.userInfo.surname}`,
        licenses: admin.licenses,
      })) ?? [],
    [admins]
  )

  return <Table columns={columns} data={data} disableGlobalSearch />
}

export function AssignLicenses({ admin, availableLicenses }: { admin: AdminData; availableLicenses?: License[] }) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()

  const assigningLicenseId = useRef<string>()
  const [openDrawer, setOpenDrawer] = useState(false)

  const [assignAdminLicenses, { isLoading: isAssigning }] = useAssignAdminLicensesMutation()

  const licenses = useMemo(
    () =>
      availableLicenses?.map((license) => {
        return {
          id: license._id,
          isAssigned: !!admin.licenses.includes(license._id),
          isAssigning: isAssigning && assigningLicenseId.current === license._id,
          name: license.name,
        }
      }) ?? [],
    [admin, availableLicenses, isAssigning]
  )

  const handleAssignLicenses = async (licenseId: string, isAssigned: boolean) => {
    try {
      assigningLicenseId.current = licenseId
      await assignAdminLicenses({ adminId: admin.id, licenses: isAssigned ? [...admin.licenses, licenseId] : _.without(admin.licenses, licenseId) }).unwrap()
      assigningLicenseId.current = undefined
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`An error has occured while assigning license. Please try again!`), { variant: 'error' })
    }
  }

  return (
    <>
      <Tooltip title={t`Assign licenses`}>
        <ChipButton onClick={() => setOpenDrawer(true)}>
          <BadgeIcon />
        </ChipButton>
      </Tooltip>
      <AssignLicenseDrawer
        licenses={licenses}
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        onAssignLicense={handleAssignLicenses}
        title={admin.fullName}
      />
    </>
  )
}

export function DeleteAdmin({ adminId }: { adminId: string }) {
  useLingui()
  const { enqueueSnackbar } = useSnackbar()

  const [deleteAdmin, { isLoading: isDeletingAdmin }] = useDeleteAdminMutation()

  const [openDialog, setOpenDialog] = useState(false)

  const handleDeleteAdmin = async () => {
    try {
      await deleteAdmin(adminId).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t`An error has occured while deleting admin. Please try again!`), { variant: 'error' })
    }
  }

  return (
    <>
      <Tooltip title={t`Delete`}>
        <ChipButton color="error" onClick={() => setOpenDialog(true)}>
          <DeleteRoundedIcon htmlColor="red" />
        </ChipButton>
      </Tooltip>
      <DeleteConfirmationDialog
        isDeleting={isDeletingAdmin}
        onClose={() => setOpenDialog(false)}
        onDelete={handleDeleteAdmin}
        open={openDialog}
        title={t`Do you want to delete this admin`}
        message={t`Please note that deleting this admin causes the loss of information. Please confirm deletion.`}
      />
    </>
  )
}
